/**
 * Vehicle Tiers Manager — editable card grid of customer-facing tiers
 * grouped by category. Click a card → edit drawer (image URL, rate, models).
 *
 * Drives /api/vehicle-tiers (server-of-truth used by public site + Quick Quote).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, X, Image as ImageIcon } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => `$${Math.round(Number(n || 0)).toLocaleString()}`;

export const CATEGORY_ORDER = ['concierge_driven', 'group_driven', 'exotic_driven'];
export const CATEGORY_LABELS = {
  concierge_driven: 'Concierge Driven',
  group_driven: 'Group Driven',
  exotic_driven: 'Exotic Driven',
};

const VehicleTiersManager = () => {
  const [categories, setCategories] = useState({});
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creatingCategory, setCreatingCategory] = useState(null);
  const [reloadTick, setReloadTick] = useState(0);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/vehicle-tiers`);
      const d = await r.json();
      setCategories(d.categories || {});
    } catch (e) { toast.error('Failed to load tiers'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load, reloadTick]);

  const deleteTier = async (id, name) => {
    if (!window.confirm(`Delete tier "${name}"? It can be restored later.`)) return;
    try {
      const r = await fetch(`${API}/api/vehicle-tiers/${id}`, { method: 'DELETE' });
      if (!r.ok) throw new Error('Delete failed');
      toast.success('Tier removed');
      setReloadTick(t => t + 1);
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid="vehicle-tiers-manager">
      <div className="flex justify-between items-start mb-4">
        <div>
          <p className="text-[9px] text-red-600 font-bold uppercase tracking-[0.3em]">Fleet · Vehicle Tiers</p>
          <h2 className="text-lg font-bold text-gray-900 mt-1">Customer-Facing Tier Hierarchy</h2>
          <p className="text-xs text-gray-500 mt-1">Drag the photo, set the rate, list the models that fit each tier. Powers the public quote selector + Quick Quote.</p>
        </div>
      </div>

      {loading && <p className="text-xs text-gray-400 text-center py-6">Loading tiers…</p>}

      {!loading && CATEGORY_ORDER.map(cat => {
        const group = categories[cat];
        const tiers = group?.tiers || [];
        return (
          <div key={cat} className="mb-6 last:mb-0" data-testid={`tier-group-${cat}`}>
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] font-bold text-gray-700 uppercase tracking-[0.2em]">{CATEGORY_LABELS[cat]} <span className="text-gray-400 normal-case">· {tiers.length} tier{tiers.length === 1 ? '' : 's'}</span></p>
              <button
                onClick={() => setCreatingCategory(cat)}
                className="text-[10px] text-red-600 font-bold uppercase tracking-wider flex items-center gap-1 hover:text-red-700"
                data-testid={`tier-add-${cat}`}
              ><Plus className="h-3 w-3" /> Add Tier</button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {tiers.map(t => (
                <button
                  key={t.tier_id}
                  onClick={() => setEditing(t)}
                  className="bg-gray-50 hover:bg-gray-100 border border-gray-200 hover:border-red-300 transition-all text-left rounded-md overflow-hidden group"
                  data-testid={`tier-card-${t.tier_id}`}
                >
                  <div className="aspect-[4/3] bg-black overflow-hidden relative">
                    {t.image_url ? (
                      <img src={t.image_url} alt={t.tier_name} className="w-full h-full object-cover" loading="lazy" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-600"><ImageIcon className="h-8 w-8" /></div>
                    )}
                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"><Edit2 className="h-3.5 w-3.5 text-white drop-shadow-md" /></div>
                  </div>
                  <div className="p-2.5">
                    <p className="text-xs font-bold text-gray-900 truncate">{t.tier_name}</p>
                    <p className="text-[10px] text-gray-500">{fmt(t.hourly_rate)}/hr · {t.pax} pax · {t.min_hours}h min</p>
                    {(t.models || []).length > 0 && (
                      <p className="text-[10px] text-gray-400 mt-1 truncate" title={t.models.join(', ')}>{t.models.slice(0, 2).join(', ')}{t.models.length > 2 ? ` +${t.models.length - 2}` : ''}</p>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        );
      })}

      {(editing || creatingCategory) && (
        <TierEditorModal
          tier={editing}
          newCategory={creatingCategory}
          onClose={() => { setEditing(null); setCreatingCategory(null); }}
          onSaved={() => { setEditing(null); setCreatingCategory(null); setReloadTick(t => t + 1); }}
          onDelete={deleteTier}
        />
      )}
    </div>
  );
};

export const TierEditorModal = ({ tier, newCategory, onClose, onSaved, onDelete }) => {
  const isNew = !tier;
  const [draft, setDraft] = useState({
    category: tier?.category || newCategory || 'concierge_driven',
    category_label: tier?.category_label || (newCategory ? CATEGORY_LABELS[newCategory] : 'Concierge Driven'),
    tier_name: tier?.tier_name || '',
    hourly_rate: tier?.hourly_rate || 0,
    min_hours: tier?.min_hours || 3,
    pax: tier?.pax || 3,
    image_url: tier?.image_url || '',
    models: (tier?.models || []).join('\n'),
    sort_order: tier?.sort_order || 999,
  });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!draft.tier_name.trim()) { toast.error('Tier name required'); return; }
    setBusy(true);
    try {
      const payload = {
        ...draft,
        category_label: CATEGORY_LABELS[draft.category] || draft.category,
        models: draft.models.split('\n').map(s => s.trim()).filter(Boolean),
        hourly_rate: Number(draft.hourly_rate),
        min_hours: Number(draft.min_hours),
        pax: Number(draft.pax),
        sort_order: Number(draft.sort_order),
      };
      const url = isNew ? `${API}/api/vehicle-tiers` : `${API}/api/vehicle-tiers/${tier.tier_id}`;
      const r = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || 'Save failed');
      toast.success(isNew ? 'Tier created' : 'Tier updated');
      onSaved();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="tier-editor-modal">
        <div className="flex justify-between items-start mb-4">
          <div>
            <p className="text-[9px] text-red-600 font-bold uppercase tracking-[0.3em]">{isNew ? 'New Tier' : 'Edit Tier'}</p>
            <h3 className="text-lg font-bold text-gray-900 mt-1">{draft.tier_name || 'Unnamed Tier'}</h3>
          </div>
          <button onClick={onClose}><X className="h-4 w-4 text-gray-400" /></button>
        </div>

        {/* Image preview */}
        {draft.image_url && (
          <div className="aspect-video bg-black rounded-md overflow-hidden mb-3">
            <img src={draft.image_url} alt="Preview" className="w-full h-full object-cover" />
          </div>
        )}

        <div className="space-y-3 text-xs">
          <div>
            <label className="block text-[10px] text-gray-500 uppercase tracking-wider font-bold mb-1">Category</label>
            <select value={draft.category} onChange={e => setDraft({ ...draft, category: e.target.value, category_label: CATEGORY_LABELS[e.target.value] })} className="w-full h-9 px-2 border border-gray-300 rounded text-xs" data-testid="tier-edit-category">
              {CATEGORY_ORDER.map(c => <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] text-gray-500 uppercase tracking-wider font-bold mb-1">Tier Name *</label>
            <input value={draft.tier_name} onChange={e => setDraft({ ...draft, tier_name: e.target.value })} placeholder="Luxury SUV" className="w-full h-9 px-2 border border-gray-300 rounded text-xs" data-testid="tier-edit-name" />
          </div>
          <div>
            <label className="block text-[10px] text-gray-500 uppercase tracking-wider font-bold mb-1">Image URL</label>
            <input value={draft.image_url} onChange={e => setDraft({ ...draft, image_url: e.target.value })} placeholder="https://customer-assets.emergentagent.com/.../..." className="w-full h-9 px-2 border border-gray-300 rounded text-xs" data-testid="tier-edit-image" />
            <p className="text-[10px] text-gray-400 mt-1">Paste any CDN URL. Use the 4:3 thumbnails you uploaded for best fit.</p>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="block text-[10px] text-gray-500 uppercase tracking-wider font-bold mb-1">Rate $/hr</label>
              <input type="number" min="0" value={draft.hourly_rate} onChange={e => setDraft({ ...draft, hourly_rate: e.target.value })} className="w-full h-9 px-2 border border-gray-300 rounded text-xs" data-testid="tier-edit-rate" />
            </div>
            <div>
              <label className="block text-[10px] text-gray-500 uppercase tracking-wider font-bold mb-1">Min Hours</label>
              <input type="number" min="1" value={draft.min_hours} onChange={e => setDraft({ ...draft, min_hours: e.target.value })} className="w-full h-9 px-2 border border-gray-300 rounded text-xs" data-testid="tier-edit-min" />
            </div>
            <div>
              <label className="block text-[10px] text-gray-500 uppercase tracking-wider font-bold mb-1">Pax</label>
              <input type="number" min="1" value={draft.pax} onChange={e => setDraft({ ...draft, pax: e.target.value })} className="w-full h-9 px-2 border border-gray-300 rounded text-xs" data-testid="tier-edit-pax" />
            </div>
          </div>
          <div>
            <label className="block text-[10px] text-gray-500 uppercase tracking-wider font-bold mb-1">Models in this tier (one per line)</label>
            <textarea value={draft.models} onChange={e => setDraft({ ...draft, models: e.target.value })} rows={4} placeholder={`Mercedes S-Class\nBMW 7-Series\nAudi A8`} className="w-full px-2 py-2 border border-gray-300 rounded text-xs font-mono" data-testid="tier-edit-models" />
          </div>
        </div>

        <div className="flex gap-2 mt-5">
          {!isNew && (
            <button onClick={() => onDelete(tier.tier_id, tier.tier_name)} className="px-3 h-9 text-[10px] font-bold uppercase tracking-wider border border-red-500 text-red-600 hover:bg-red-50 rounded" data-testid="tier-edit-delete"><Trash2 className="h-3 w-3 inline mr-1" />Delete</button>
          )}
          <div className="flex-1" />
          <button onClick={onClose} className="px-3 h-9 text-[10px] font-bold uppercase tracking-wider border border-gray-300 text-gray-600 hover:bg-gray-50 rounded" data-testid="tier-edit-cancel">Cancel</button>
          <button onClick={submit} disabled={busy} className="px-4 h-9 text-[10px] font-bold uppercase tracking-wider bg-red-600 hover:bg-red-700 text-white rounded disabled:opacity-50" data-testid="tier-edit-save">{busy ? 'Saving…' : isNew ? 'Create' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
};

export default VehicleTiersManager;
